import { Box, Divider, Flex, Select, Spinner, Text } from '@chakra-ui/react'
import { useFirebase } from '@chem-po/firebase-web'
import { MIDWIVES, objectToArray, PracticeUserAccess, USER_ROLES, WithId } from '@hb/shared'
import { collection, query, Query, where } from 'firebase/firestore'
import { useCallback, useMemo, useState } from 'react'
import { useQuery } from '../../../hooks/backend/useQuery'
import { usePracticeContractsStatus } from '../../../hooks/practiceContracts/usePracticeContractsStatus'
import { SolidActionButton } from '../../Buttons/ActionButton'
import { DefaultModal } from '../../Modals'
import { PracticeContractComposeModal } from './PracticeContractComposeModal'
import { PracticeContractsStatusAdminView } from './PracticeContractsStatus'

const providerLabel = (provider: WithId<PracticeUserAccess>) => {
  const name = [provider.fname, provider.lname].filter(Boolean).join(' ')
  return name ? `${name} (${provider.email})` : provider.email
}

const SendPracticeContractView = ({ practiceId }: { practiceId: string }) => {
  const { firestore } = useFirebase()
  const [selectedProviderId, setSelectedProviderId] = useState<string>('')
  const [composing, setComposing] = useState(false)

  const providersQuery = useMemo(
    () =>
      query(
        collection(firestore, MIDWIVES, practiceId, USER_ROLES),
        where('role', '==', 'super-admin'),
      ) as Query<PracticeUserAccess>,
    [firestore, practiceId],
  )
  const { data: providersMap, loading: providersLoading } = useQuery(providersQuery)
  const providers = useMemo(
    () => objectToArray(providersMap ?? {}) as WithId<PracticeUserAccess>[],
    [providersMap],
  )

  // practice-wide contract list (all providers)
  const status = usePracticeContractsStatus(practiceId)
  const getSignerLabel = useCallback(
    (uid: string) => {
      const provider = providersMap?.[uid]
      return provider ? providerLabel({ ...provider, id: uid }) : uid
    },
    [providersMap],
  )

  return (
    <Flex flexDir="column" w="100%" p={4} gap={3}>
      <Text fontSize="lg" fontWeight={600} fontFamily="Hero-New">
        Practice Contracts
      </Text>
      {providersLoading ? (
        <Flex align="center" gap={2}>
          <Spinner size="sm" /> Loading providers...
        </Flex>
      ) : providers.length ? (
        <Flex
          gap={2}
          flexFlow={['column', 'column', 'row']}
          align={['stretch', 'stretch', 'center']}>
          <Select
            flex={1}
            placeholder="Select a practice super admin"
            value={selectedProviderId}
            onChange={e => setSelectedProviderId(e.target.value)}>
            {providers.map(p => (
              <option key={p.id} value={p.id}>
                {providerLabel(p)}
              </option>
            ))}
          </Select>
          <SolidActionButton
            size="sm"
            onClick={() => setComposing(true)}
            isDisabled={!selectedProviderId}>
            Send Contract
          </SolidActionButton>
        </Flex>
      ) : (
        <Text color="gray.500">This practice has no super-admin providers.</Text>
      )}

      <Divider />
      <Box w="100%">
        <Text fontWeight={500}>Sent contracts</Text>
        <PracticeContractsStatusAdminView status={status} getSignerLabel={getSignerLabel} />
      </Box>

      {composing && selectedProviderId ? (
        <PracticeContractComposeModal
          practiceId={practiceId}
          signerId={selectedProviderId}
          onClose={() => setComposing(false)}
        />
      ) : null}
    </Flex>
  )
}

export const SendPracticeContractModal = ({
  practiceId,
  onClose,
}: {
  practiceId: string
  onClose: () => void
}) => (
  <DefaultModal
    isOpen
    size="3xl"
    overlayHeader
    onClose={onClose}
    render={() => <SendPracticeContractView practiceId={practiceId} />}
  />
)
